import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import adminApi from '../services/adminApi';
import { useAdminSeasonPhaseFilter } from '../hooks/useAdminSeasonPhaseFilter';
import AdminSeasonPhaseFilters from './AdminSeasonPhaseFilters';

// Large seasons used to time out: DB updated every team with audit triggers. Migration 028 makes lock instant.
const LOCK_REQUEST_MS = 180000;

// API functions - using adminApi which includes authentication headers
const divisionLockAPI = {
  getStatus: (seasonId) => {
    const url = seasonId 
      ? `/division-lock-system/division-lock-status?seasonId=${seasonId}`
      : `/division-lock-system/division-lock-status`;
    return adminApi.get(url);
  },
  toggleLock: (adminUser, seasonId) =>
    adminApi.post(
      `/division-lock-system/toggle-division-lock`,
      { adminUser, seasonId },
      { timeout: LOCK_REQUEST_MS }
    ),
  lockDivisions: (adminUser, reason, seasonId) =>
    adminApi.post(
      `/division-lock-system/lock-divisions`,
      { adminUser, reason, seasonId },
      { timeout: LOCK_REQUEST_MS }
    ),
  unlockDivisions: (adminUser, seasonId) =>
    adminApi.post(
      `/division-lock-system/unlock-divisions`,
      { adminUser, seasonId },
      { timeout: LOCK_REQUEST_MS }
    ),
  getAllSeasons: () => adminApi.get('/division-lock-system/all-seasons-lock-status')
};

// Styled Components
const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const StatusCard = styled.div`
  background: ${props => props.$isLocked ? '#fef2f2' : '#f0fdf4'};
  border: 1px solid ${props => props.$isLocked ? '#fecaca' : '#bbf7d0'};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  
  .status-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    
    .icon {
      font-size: 1.5rem;
    }
    
    .text {
      font-weight: 600;
      font-size: 1.125rem;
      color: ${props => props.$isLocked ? '#dc2626' : '#16a34a'};
    }
  }
  
  .description {
    color: #6b7280;
    font-size: 0.875rem;
  }
`;

const ToggleButton = styled.button`
  background: ${props => props.$isLocked ? '#16a34a' : '#dc2626'};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 200px;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.$isLocked ? '#15803d' : '#b91c1c'};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const AdvancedControls = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const ControlButton = styled.button`
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #043663;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const InfoItem = styled.div`
  .label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .value {
    font-size: 0.875rem;
    color: #1f2937;
    margin-top: 0.25rem;
  }
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &.success {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #166534;
  }
  
  &.error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
  }
  
  &.warning {
    background: #fffbeb;
    border: 1px solid #fed7aa;
    color: #d97706;
  }
  
  &.info {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1d4ed8;
  }
`;

const SeasonsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const SeasonCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 1rem;
  
  .season-name {
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }
  
  .status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }
  
  .status-badge {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    
    &.locked {
      background: #fef2f2;
      color: #dc2626;
    }
    
    &.unlocked {
      background: #f0fdf4;
      color: #16a34a;
    }
  }
  
  .teams-info {
    font-size: 0.875rem;
    color: #6b7280;
  }
`;

// eslint-disable-next-line no-unused-vars
const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: #6b7280;
`;

const SeasonSelector = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  margin-bottom: 1rem;
  
  &:hover {
    border-color: #043663;
  }
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
`;

const DivisionLockSystem = ({ adminUser = 'admin' }) => {
  const [message, setMessage] = useState(null);
  const queryClient = useQueryClient();
  const {
    campaignId,
    seasonId: selectedSeasonId,
    onCampaignChange,
    onPhaseChange,
    seasonOptions,
    phaseOptions,
    isLoading: filterLoading,
  } = useAdminSeasonPhaseFilter({ allowAll: false, autoDefault: true });

  // Query for all seasons status
  const { data: allSeasonsStatus, isLoading: allSeasonsLoading, error: allSeasonsError } = useQuery(
    ['all-seasons-lock-status'],
    () => divisionLockAPI.getAllSeasons(),
    {
      refetchInterval: 60000, // Refetch every minute
      retry: false, // Don't retry on error
    }
  );

  // Query for division lock status - use selected season or default
  const { data: lockStatusData, isLoading: statusLoading, error: statusError, refetch: refetchStatus } = useQuery(
    ['division-lock-status', selectedSeasonId],
    () => divisionLockAPI.getStatus(selectedSeasonId),
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      retry: false, // Don't retry on error
      enabled: !!selectedSeasonId,
    }
  );

  // Get current season from lock status response
  // adminApi returns response.data directly, so structure is: { success, data, seasonId, seasonName }
  const currentSeasonId = lockStatusData?.seasonId || selectedSeasonId;
  const currentSeasonName = lockStatusData?.seasonName || 'Unknown Phase';
  const lockStatus = lockStatusData?.data;

  const filteredLockSeasons = useMemo(() => {
    const rows = allSeasonsStatus?.data || [];
    if (!rows.length) return [];
    if (!campaignId) return rows;
    const allowed = new Set(phaseOptions.map((p) => String(p.id)));
    return rows.filter((s) => allowed.has(String(s.season_id)));
  }, [allSeasonsStatus, campaignId, phaseOptions]);

  // Debug logging
  useEffect(() => {
    console.log('🔍 DivisionLockSystem - Lock status data:', lockStatusData);
    console.log('🔍 DivisionLockSystem - Current season ID:', currentSeasonId);
    console.log('🔍 DivisionLockSystem - Current season name:', currentSeasonName);
    console.log('🔍 DivisionLockSystem - Lock status:', lockStatus);
    console.log('🔍 DivisionLockSystem - Is locked:', lockStatus?.is_locked);
  }, [lockStatusData, currentSeasonId, currentSeasonName, lockStatus]);

  // Toggle lock mutation
  const toggleLockMutation = useMutation(
    () => divisionLockAPI.toggleLock(adminUser, selectedSeasonId),
    {
      onSuccess: (response) => {
        console.log('🔍 Toggle success response:', response);
        // adminApi returns response.data directly
        const data = response?.data || response;
        
        // Check if the operation was successful
        if (data.success === false) {
          setMessage({
            type: 'error',
            text: data.message || 'Failed to toggle division lock',
            details: data.error || 'Unknown error'
          });
          return;
        }
        
        // Safely access result properties
        const result = data.result || {};
        const teamsAffected = result.teams_locked || result.teams_unlocked || 0;
        
        setMessage({
          type: data.action === 'locked' ? 'success' : 'warning',
          text: data.message || 'Division lock toggled successfully',
          details: `Teams: ${teamsAffected} affected`
        });
        console.log('🔍 Invalidating queries...');
        queryClient.invalidateQueries(['division-lock-status']);
        queryClient.invalidateQueries(['all-seasons-lock-status']);
        console.log('🔍 Queries invalidated');
      },
      onError: (error) => {
        console.error('🔍 Toggle error:', error);
        setMessage({
          type: 'error',
          text: `Failed to toggle division lock: ${error.response?.data?.error || error.message}`
        });
      }
    }
  );

  // Lock divisions mutation
  const lockDivisionsMutation = useMutation(
    ({ reason }) => divisionLockAPI.lockDivisions(adminUser, reason, selectedSeasonId),
    {
      onSuccess: (response) => {
        // adminApi returns response.data directly
        const data = response?.data || response;
        setMessage({
          type: 'success',
          text: `🔒 Divisions locked! ${data.teams_locked} teams are now locked.`,
          details: 'Teams cannot change divisions, skill levels, or members.'
        });
        queryClient.invalidateQueries(['division-lock-status']);
        queryClient.invalidateQueries(['all-seasons-lock-status']);
      },
      onError: (error) => {
        setMessage({
          type: 'error',
          text: `Failed to lock divisions: ${error.response?.data?.error || error.message}`
        });
      }
    }
  );

  // Unlock divisions mutation
  const unlockDivisionsMutation = useMutation(
    () => divisionLockAPI.unlockDivisions(adminUser, selectedSeasonId),
    {
      onSuccess: (response) => {
        // adminApi returns response.data directly
        const data = response?.data || response;
        setMessage({
          type: 'warning',
          text: `🔓 Divisions unlocked! ${data.teams_unlocked} teams can now change divisions.`,
          details: '⚠️ Use "Regenerate Matches" after making changes.'
        });
        queryClient.invalidateQueries(['division-lock-status']);
        queryClient.invalidateQueries(['all-seasons-lock-status']);
      },
      onError: (error) => {
        setMessage({
          type: 'error',
          text: `Failed to unlock divisions: ${error.response?.data?.error || error.message}`
        });
      }
    }
  );

  const handleToggleLock = () => {
    console.log('🔍 Toggle button clicked!');
    console.log('🔍 Current isLocked state:', isLocked);
    console.log('🔍 Mutation loading state:', toggleLockMutation.isLoading);
    toggleLockMutation.mutate();
  };

  const handleLockDivisions = () => {
    const reason = prompt('Enter reason for locking divisions (optional):') || 'Manual lock';
    lockDivisionsMutation.mutate({ reason });
  };

  const handleUnlockDivisions = () => {
    if (window.confirm('Are you sure you want to unlock divisions? Teams will be able to change divisions and skill levels.')) {
      unlockDivisionsMutation.mutate();
    }
  };

  const isLocked = lockStatus?.is_locked || false;
  const isLoading = toggleLockMutation.isLoading || lockDivisionsMutation.isLoading || unlockDivisionsMutation.isLoading;
  
  // Friendly message when no active/registration season (backend returns 200 with error)
  const noSeasonMessage = lockStatusData?.success && lockStatusData?.error ? lockStatusData.error : null;
  const apiErrorMessage = statusError?.response?.data?.error || statusError?.message;

  return (
    <Container>
      <Header>
        <span style={{ fontSize: '2rem' }}>🔒</span>
        <h1>Division Lock System</h1>
      </Header>

      {message && (
        <Alert className={message.type}>
          <span>{message.type === 'success' ? '✅' : message.type === 'error' ? '❌' : message.type === 'warning' ? '⚠️' : 'ℹ️'}</span>
          <div>
            <div>{message.text}</div>
            {message.details && <div style={{ fontSize: '0.875rem', marginTop: '0.25rem', opacity: 0.8 }}>{message.details}</div>}
          </div>
        </Alert>
      )}

      {noSeasonMessage && !statusLoading && (
        <Alert className="info">
          <span>ℹ️</span>
          <div>
            <div>This page cannot load division lock status.</div>
            <div style={{ fontSize: '0.875rem', marginTop: '0.25rem', opacity: 0.8 }}>
              {noSeasonMessage}
            </div>
            <div style={{ fontSize: '0.875rem', marginTop: '0.5rem', opacity: 0.8 }}>
              Create or link a phase under <strong>Settings → Tournaments</strong> to use division lock.
            </div>
          </div>
        </Alert>
      )}

      {apiErrorMessage && !noSeasonMessage && !statusLoading && (
        <Alert className="error">
          <span>❌</span>
          <div>
            <div>Could not load division lock status</div>
            <div style={{ fontSize: '0.875rem', marginTop: '0.25rem', opacity: 0.8 }}>{apiErrorMessage}</div>
          </div>
        </Alert>
      )}

      <Card>
        <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', fontWeight: '600' }}>
          Phase Division Control
        </h2>
        
        <div style={{ marginBottom: '1.5rem' }}>
          <AdminSeasonPhaseFilters
            campaignId={campaignId}
            seasonId={selectedSeasonId || ''}
            seasonOptions={seasonOptions}
            phaseOptions={phaseOptions}
            onCampaignChange={(id) => {
              onCampaignChange(id);
              queryClient.invalidateQueries(['division-lock-status']);
            }}
            onPhaseChange={(id) => {
              onPhaseChange(id);
              queryClient.invalidateQueries(['division-lock-status']);
            }}
            allowAll={false}
            disabled={filterLoading}
          />
          {!selectedSeasonId && !filterLoading && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
              {noSeasonMessage || 'Select a Season and Phase to lock or unlock divisions.'}
            </div>
          )}
        </div>
        
        {currentSeasonId && !noSeasonMessage ? (
          <div>
            {/* Season Name Display */}
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Selected Phase:</div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>{currentSeasonName}</div>
            </div>
            
            {/* Current Status */}
            <StatusCard $isLocked={isLocked}>
              <div className="status-header">
                <span className="icon">{isLocked ? '🔒' : '🔓'}</span>
                <span className="text">{isLocked ? 'Divisions LOCKED' : 'Divisions UNLOCKED'}</span>
              </div>
              <div className="description">
                {isLocked 
                  ? 'Teams cannot change divisions, skill levels, or members.'
                  : 'Teams can change divisions, skill levels, and members.'
                }
              </div>
            </StatusCard>

            {/* Single Toggle Button */}
            <div style={{ marginBottom: '1.5rem' }}>
              <ToggleButton
                $isLocked={isLocked}
                onClick={handleToggleLock}
                disabled={isLoading || statusLoading || !selectedSeasonId}
              >
                {isLoading ? '⏳ Processing...' : (isLocked ? '🔓 Unlock Divisions' : '🔒 Lock Divisions')}
              </ToggleButton>
              {isLoading && (
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8125rem', color: '#6b7280' }}>
                  Applying lock state on the server… If this hangs past a few minutes, check server logs or run DB migration
                  028 (division lock fast path).
                </p>
              )}
            </div>

            {/* Advanced Controls */}
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600' }}>
                Advanced Controls
              </h3>
              
              <AdvancedControls>
                <ControlButton
                  onClick={handleLockDivisions}
                  disabled={isLoading || isLocked || !selectedSeasonId}
                >
                  🔒 Force Lock
                </ControlButton>
                
                <ControlButton
                  onClick={handleUnlockDivisions}
                  disabled={isLoading || !isLocked || !selectedSeasonId}
                >
                  🔓 Force Unlock
                </ControlButton>
                
                <ControlButton
                  onClick={() => refetchStatus()}
                  disabled={isLoading || !selectedSeasonId}
                >
                  🔄 Refresh Status
                </ControlButton>
              </AdvancedControls>
            </div>
          </div>
        ) : !noSeasonMessage ? (
          <Alert className="info">
            <span>ℹ️</span>
            <div>
              <div>No phase selected for division lock management.</div>
              <div style={{ fontSize: '0.875rem', marginTop: '0.25rem', opacity: 0.8 }}>
                Please select a phase from the dropdown above to manage division locks.
                {allSeasonsStatus?.data && allSeasonsStatus.data.length === 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    No phases found in the system. Please create a phase first.
                  </div>
                )}
              </div>
            </div>
          </Alert>
        ) : null}
      </Card>

      {/* Status Information */}
      <Card>
        <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', fontWeight: '600' }}>
          Current Status
        </h2>
        
        {lockStatus?.data?.data ? (
          <StatusInfo>
            <InfoItem>
              <div className="label">Status</div>
              <div className="value">
                <span className={`status-badge ${isLocked ? 'locked' : 'unlocked'}`}>
                  {isLocked ? '🔒 LOCKED' : '🔓 UNLOCKED'}
                </span>
              </div>
            </InfoItem>
            
            <InfoItem>
              <div className="label">Teams Total</div>
              <div className="value">{lockStatus.data.data.teams_total || 0}</div>
            </InfoItem>
            
            <InfoItem>
              <div className="label">Teams Locked</div>
              <div className="value">{lockStatus.data.data.teams_locked || 0}</div>
            </InfoItem>
            
            {lockStatus.data.data.locked_at && (
              <InfoItem>
                <div className="label">Locked At</div>
                <div className="value">{new Date(lockStatus.data.data.locked_at).toLocaleString()}</div>
              </InfoItem>
            )}
            
            {lockStatus.data.data.unlocked_at && (
              <InfoItem>
                <div className="label">Unlocked At</div>
                <div className="value">{new Date(lockStatus.data.data.unlocked_at).toLocaleString()}</div>
              </InfoItem>
            )}
            
            {lockStatus.data.data.locked_by && (
              <InfoItem>
                <div className="label">Locked By</div>
                <div className="value">{lockStatus.data.data.locked_by}</div>
              </InfoItem>
            )}
            
            {lockStatus.data.data.lock_reason && (
              <InfoItem>
                <div className="label">Reason</div>
                <div className="value">{lockStatus.data.data.lock_reason}</div>
              </InfoItem>
            )}
          </StatusInfo>
        ) : (
          <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            No status information available
          </div>
        )}
      </Card>

      {/* All Seasons Overview */}
      <Card>
        <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', fontWeight: '600' }}>
          All Phases Overview
        </h2>
        
        {allSeasonsError ? (
          <Alert className="error">
            <span>❌</span>
            <div>
              <div>Error loading phases: {allSeasonsError.message}</div>
              <div style={{ fontSize: '0.875rem', marginTop: '0.25rem', opacity: 0.8 }}>
                Make sure the server is running and the database is accessible.
              </div>
            </div>
          </Alert>
        ) : filteredLockSeasons.length > 0 ? (
          <SeasonsGrid>
            {filteredLockSeasons.map((season, index) => {
              const isSelected = selectedSeasonId === season.season_id;
              const isRegistrationOpen = (() => {
                const now = new Date();
                const regStart = new Date(season.registration_start);
                const regEnd = new Date(season.registration_end);
                return now >= regStart && now <= regEnd && season.status !== 'completed';
              })();
              const isActive = (() => {
                const now = new Date();
                const start = new Date(season.start_date);
                const end = new Date(season.end_date);
                return now >= start && now <= end && season.status !== 'completed';
              })();
              
              return (
                <SeasonCard 
                  key={`season-${season.season_id}-${index}`}
                  onClick={() => onPhaseChange(String(season.season_id))}
                  style={{
                    cursor: 'pointer',
                    border: isSelected ? '2px solid #043663' : '1px solid #e5e7eb',
                    background: isSelected ? '#f0f9ff' : 'white',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = '#043663';
                      e.currentTarget.style.background = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.background = 'white';
                    }
                  }}
                >
                  <div className="season-name">
                    {season.season_name}
                    {isSelected && <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#043663' }}>✓ Selected</span>}
                  </div>
                  
                  <div className="status">
                    <span className={`status-badge ${season.is_locked ? 'locked' : 'unlocked'}`}>
                      {season.is_locked ? '🔒 LOCKED' : '🔓 UNLOCKED'}
                    </span>
                    {isActive && <span style={{ fontSize: '0.75rem', color: '#16a34a', marginLeft: '0.5rem' }}>(Active)</span>}
                    {isRegistrationOpen && !isActive && <span style={{ fontSize: '0.75rem', color: '#2563eb', marginLeft: '0.5rem' }}>(Registration Open)</span>}
                    {season.status === 'completed' && <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem' }}>(Completed)</span>}
                  </div>
                  
                  <div className="teams-info">
                    Teams: {season.total_teams} ({season.locked_teams} locked)
                  </div>
                  
                  {season.locked_at && (
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                      Locked: {new Date(season.locked_at).toLocaleDateString()}
                    </div>
                  )}
                  
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', fontStyle: 'italic' }}>
                    Click to select
                  </div>
                </SeasonCard>
              );
            })}
          </SeasonsGrid>
        ) : allSeasonsLoading ? (
          <div style={{ color: '#6b7280' }}>Loading phases…</div>
        ) : (
          <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            No phases to show for this Season filter.
          </div>
        )}
      </Card>

      {/* Information Panel */}
      <Card>
        <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>ℹ️</span>
          How Division Locking Works
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
          <div>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>
              When Divisions are LOCKED:
            </h3>
            <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#374151' }}>
              <li>Teams cannot change divisions</li>
              <li>Team member skill level changes won't affect division assignment</li>
              <li>Admin panel cannot manually change team divisions</li>
              <li>All division changes are blocked at the database level</li>
              <li>Existing matches remain unaffected</li>
            </ul>
          </div>
          
          <div>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>
              When Divisions are UNLOCKED:
            </h3>
            <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#374151' }}>
              <li>Teams can change divisions based on skill levels</li>
              <li>Team member updates can trigger division changes</li>
              <li>Admin panel can manually change team divisions</li>
              <li>Use "Regenerate Matches" after making changes</li>
              <li>Consider locking again after changes are complete</li>
            </ul>
          </div>
        </div>
      </Card>
    </Container>
  );
};

export default DivisionLockSystem;